import { ChangeEvent, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import InputMask from "react-input-mask";
import { CancellationCreateData } from "../../../services/api-tansquery/cancellation/create";
import { Cancellation } from "../../../@types/cancellation";
import UnityField from "./components/unity-field";
import { Divider, FormControl, FormControlLabel, FormHelperText, Switch } from "@material-ui/core";
import CalendarToday from "@material-ui/icons/CalendarToday";

type FormProps = {
    cancellation?: Cancellation;
    mutation?: UseMutationResult<
        Cancellation,
        unknown,
        | CancellationCreateData,
        unknown
    >
};

export default function Form({ cancellation, mutation }: FormProps) {
    const history = useHistory();
    const { classes } = useFormStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            ativo: true,
            data: "",
            geral: false,
            online: false,
            presencial: false,
            reagendar_presencial: false,
            titulo: "",
            texto: "",
            slug_unidade: undefined,
        }
    });
    const watchedGeral = form.watch("geral");

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            ativo: data.ativo,
            data: data.data.split("/").reverse().join("-"),
            geral: data.geral,
            online: data.online,
            presencial: data.presencial,
            reagendar_presencial: data.reagendar_presencial,
            titulo: data.titulo,
            texto: data.texto,
            slug_unidade: data.slug_unidade,
        });
    }

    useEffect(() => {
        if (cancellation) {
            form.reset({
                ativo: cancellation.ativo || false,
                data: cancellation.data.split("-").reverse().join("/") || "",
                geral: cancellation.geral || false,
                online: cancellation.online || false,
                presencial: cancellation.presencial || false,
                reagendar_presencial: cancellation.reagendar_presencial || false,
                titulo: cancellation.titulo || "",
                texto: cancellation.texto || "",
                slug_unidade: cancellation.unidade?.slug_unidade || undefined,
            });
        }
    }, [form, cancellation]);

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    useEffect(() => {
        if (!!watchedGeral) {
            form.setValue("slug_unidade", undefined);
        }
    }, [watchedGeral, form]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Typography className={classes.title}>
                Gerenciar Cancelamento
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        name="titulo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Título"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Nome curto e claro que será exibido nos registros e notificações."}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="geral"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <FormControl error={!!fieldState.error} component="fieldset" fullWidth>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={!!field.value}
                                        />
                                    }
                                    label="Cancelamento Geral"
                                />
                                <FormHelperText>
                                    Ao marcar, o campo de unidade será desabilitado.
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Controller
                        name="slug_unidade"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <UnityField
                                field={field}
                                fieldState={fieldState}
                                disabled={!!watchedGeral}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Controller
                        name="data"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <InputMask
                                mask="99/99/9999"
                                maskChar={null}
                                value={field.value || ""}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(e.target.value);
                                }}
                            >
                                {(inputProps: any) => (
                                    <TextField
                                        {...inputProps}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        label="Data de Cancelamento"
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error ? fieldState.error.message : "A data em que os agendamentos serão cancelados."
                                        }
                                        InputProps={{
                                            required: false,
                                            endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                        }}
                                    />
                                )}
                            </InputMask>
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box my={2}>
                        <Divider />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Typography>Configuração de Atendimento</Typography>
                </Grid>
                <Grid item xs={12} spacing={2}>
                    <Box ml={3}>
                        <Controller
                            name="reagendar_presencial"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormControl error={!!fieldState.error} component="fieldset" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={!!field.value}
                                            />
                                        }
                                        label="Reagendar Atendimentos Presenciais?"
                                    />
                                    <FormHelperText>
                                        Ao marcar, será enviado um link para o cidadão escolher um novo horário.
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} spacing={2}>
                    <Box>
                        <Paper
                            elevation={0}
                            style={{
                                background: "#f9f9f9",
                                borderLeft: "6px solid #fdcd86",
                                padding: 16,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" style={{ color: "#a67c52" }}>
                                É necessário que marque pelo menos uma alternativa abaixo.
                            </Typography>
                        </Paper>
                    </Box>
                </Grid>
                <Grid item xs={12} spacing={2}>
                    <Box ml={3}>
                        <Controller
                            name="presencial"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormControl error={!!fieldState.error} component="fieldset" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={!!field.value}
                                            />
                                        }
                                        label="Atendimentos Presenciais"
                                    />
                                    <FormHelperText>
                                        Marque se este cancelamento impacta o atendimento físico.
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box ml={3}>
                        <Controller
                            name="online"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <FormControl error={!!fieldState.error} component="fieldset" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={!!field.value}
                                            />
                                        }
                                        label="Atendimentos Online"
                                    />
                                    <FormHelperText>
                                        Marque se este cancelamento impacta o atendimento digital.
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box my={2}>
                        <Divider />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="texto"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Motivo"
                                variant="outlined"
                                multiline
                                minRows={5}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Essa informação será registrada para consulta administrativa."}
                                InputProps={{
                                    required: false,
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
